<!-- No Cancel: the header cross closes without saving, and the footer keeps
     the two decisions that write -- DELETE beside SAVE, on one row. -->
<app-shared-dialog
  [title]="'knowledge_base.price_observations.edit.title' | translate"
  [options]="{
    size: 'medium',
    hideCancelButton: true,
    middleText: 'common.delete' | translate,
    middleIcon: 'trash-cc',
    middleButtonStyle: 'danger',
    confirmText: 'knowledge_base.price_observations.edit.save' | translate,
    confirmDisabled: !canSave
  }"
  (cancel)="cancel()"
  (middle)="remove()"
  (confirm)="save()"
>
  <div class="observation-edit">
    <!-- An unsettled conflict says WHAT it disagrees with, and GARDER CE PRIX
         settles it server-side — the rival lands Remplacé. -->
    <div class="observation-edit__conflict" *ngIf="isConflict">
      <div class="observation-edit__conflict-text">
        <strong>{{ "knowledge_base.price_observations.conflict.banner_title" | translate }}</strong>
        <span>
          {{ "knowledge_base.price_observations.conflict.banner_body" | translate: { rival: rivalLabel } }}
        </span>
        <!-- WHERE the other price comes from. Two amounts and a date are not
             something an operator can arbitrate; the document each was read
             from is. -->
        <span class="observation-edit__conflict-source" *ngIf="rivalSource">
          {{ "knowledge_base.price_observations.conflict.rival_source" | translate }} {{ rivalSource }}
        </span>
        <p class="observation-edit__quote" *ngIf="rivalQuote">{{ rivalQuote }}</p>
      </div>
      <ui-button
        class="observation-edit__conflict-action"
        type="secondary"
        [label]="'knowledge_base.price_observations.conflict.keep_price' | translate"
        (click)="keepPrice()"
      >
      </ui-button>
    </div>

    <!-- Provenance, read-only: an observation whose citation could be
         rewritten would stop being evidence.
         The block shows as soon as the row knows WHERE it came from: a file
         and a row number with no verbatim line is a poorer answer than the
         quote, but it is still the answer to "where does this price come
         from". -->
    <div class="observation-edit__evidence" *ngIf="hasQuote || sourceLine">
      <span class="observation-edit__evidence-label">
        {{ "knowledge_base.price_observations.resolve.evidence" | translate }}
        <ng-container *ngIf="sourceLine"> — {{ sourceLine }}</ng-container>
      </span>
      <p class="observation-edit__quote" *ngIf="hasQuote">{{ observation.source_quote }}</p>
    </div>

    <!-- Client and scope, editable even on an attached row. Moving either
         re-teaches the matcher server-side — always. -->
    <div class="observation-edit__fields">
      <!-- The audience is a decision the operator can change; the merge
           replays under the new key and a contradiction shows as a conflict.
           It comes FIRST because it decides whether the next field exists at
           all: a global row has no customer, and offering the picker beside
           "All customers" asked a question with no answer. Same order and
           same rule as the import wizard's scope step. -->
      <div class="standard-input observation-edit__audience">
        <!-- ONE customer: this dialog edits a single price row, and a price is
             for one customer. The upload's picker takes several. -->
        <app-audience-scope-picker
          [options]="scopeAudiences"
          [value]="audienceScope"
          [maxCustomers]="1"
          (valueChange)="onAudienceScopeChange($event)"
        ></app-audience-scope-picker>
      </div>

      <div class="standard-input observation-edit__product">
        <label for="observation-product">{{ "knowledge_base.price_observations.col.product" | translate }}</label>
        <input
          id="observation-product"
          type="text"
          class="is-autocomplete"
          [formControl]="productControl"
          [matAutocomplete]="productAuto"
          [placeholder]="'knowledge_base.price_observations.resolve.product_placeholder' | translate"
        />
        <mat-autocomplete
          #productAuto="matAutocomplete"
          [displayWith]="displayOptionFn"
          (optionSelected)="onProductSelected($event.option.value)"
        >
          <mat-option *ngFor="let option of productOptions$ | async" [value]="option">
            {{ option.label }}
          </mat-option>
        </mat-autocomplete>
        <!-- Matcher SUGGESTIONS for an unresolved product: applying one fills
             the field above, SAVE confirms it — the human decides, always. A
             sku the local catalogue does not hold is shown but not clickable.

             Full-width rows, in the matcher's own order, and NO percentage:
             chips wrapped a three-candidate list over three ragged lines, and
             the score they carried was a reranker's number an operator cannot
             act on — worse, a candidate the matcher scored at nothing read
             "(0%)" and looked like noise beside the two real answers. The
             ORDER is the ranking; only the first is named as such. -->
        <div class="observation-edit__suggestions" *ngIf="suggestions.length">
          <span class="observation-edit__suggestions-label">
            {{ "knowledge_base.price_observations.edit.suggestions_label" | translate }}
          </span>
          <button
            *ngFor="let candidate of suggestions; let first = first"
            type="button"
            class="observation-edit__suggestion"
            [class.is-picked]="isPicked(candidate)"
            [disabled]="candidate.product_id == null"
            [matTooltip]="candidate.product_id == null
              ? ('knowledge_base.price_observations.edit.suggestion_not_in_catalogue' | translate)
              : ''"
            matTooltipPosition="above"
            (click)="applySuggestion(candidate)"
          >
            <span class="observation-edit__suggestion-sku">{{ candidate.sku }}</span>
            <span class="observation-edit__suggestion-name">{{ candidate.name || "" }}</span>
            <span *ngIf="first" class="observation-edit__suggestion-best">
              {{ "knowledge_base.price_observations.edit.suggestion_best" | translate }}
            </span>
            <span *ngIf="candidate.product_id == null" class="observation-edit__suggestion-absent">
              {{ "knowledge_base.price_observations.edit.suggestion_not_in_catalogue_short" | translate }}
            </span>
          </button>
        </div>
      </div>

      <div class="standard-input">
        <label for="observation-price">{{ "knowledge_base.price_observations.col.price" | translate }}</label>
        <input id="observation-price" type="number" step="0.0001" [(ngModel)]="price" />
      </div>
      <!-- Editable, and it has to be: the mapping screen lets an operator name
           a currency column, the reader ASSUMES the company currency when it
           cannot read one (`currency_assumed`), and until now the row that
           said so could be corrected everywhere except here. The route has
           accepted `currency_id` all along. -->
      <div class="standard-input">
        <label>{{ "knowledge_base.price_observations.col.currency" | translate }}</label>
        <ui-form-field
          type="select"
          [formControl]="currencyControl"
          [options]="currencyOptions"
          [fullWidth]="true"
          [placeholder]="'knowledge_base.price_observations.col.currency' | translate"
        ></ui-form-field>
      </div>
      <div class="standard-input">
        <label for="observation-observed-at">{{ "knowledge_base.price_observations.col.observed_at" | translate }}</label>
        <input id="observation-observed-at" type="date" [(ngModel)]="observedAt" />
      </div>
      <div class="standard-input">
        <label for="observation-valid-until">{{ "knowledge_base.price_observations.col.valid_until" | translate }}</label>
        <input id="observation-valid-until" type="date" [(ngModel)]="validUntil" />
      </div>

      <!-- The three remaining fields the MAPPING screen asks about. A file can
           name a unit, a customer's own product code and a description; before
           this the operator could map all three and correct none. -->
      <!-- CHOSEN, not typed: the unit is matched against the product's own
           handling units by name and exactly, so a typed one that the
           catalogue does not carry resolves to nothing — silently. A unit the
           file wrote and the catalogue does not know stays in the list,
           marked, so it can be read and replaced by one that resolves. -->
      <div class="standard-input">
        <label>{{ "knowledge_base.price_observations.col.unit" | translate }}</label>
        <ui-form-field
          type="select"
          [formControl]="unitControl"
          [options]="unitOptions"
          [fullWidth]="true"
          [placeholder]="'knowledge_base.price_observations.edit.unit_placeholder' | translate"
        ></ui-form-field>
        <p class="observation-edit__field-note" *ngIf="unitNeedsProduct">
          {{ "knowledge_base.price_observations.edit.unit_needs_product" | translate }}
        </p>
        <p class="observation-edit__field-note observation-edit__field-note--warn"
           *ngIf="!unitNeedsProduct && unitUnresolved">
          {{ "knowledge_base.price_observations.edit.unit_unresolved" | translate: { unit: observation.unit } }}
        </p>
      </div>
      <div class="standard-input">
        <label for="observation-customer-code">
          {{ "knowledge_base.import.mapping.role.customer_code" | translate }}
        </label>
        <input
          id="observation-customer-code"
          type="text"
          [(ngModel)]="customerCodeRaw"
          [placeholder]="'knowledge_base.price_observations.edit.customer_code_placeholder' | translate"
        />
      </div>
      <div class="standard-input observation-edit__description">
        <label for="observation-description">
          {{ "knowledge_base.import.mapping.role.product_name" | translate }}
        </label>
        <input
          id="observation-description"
          type="text"
          [(ngModel)]="productNameRaw"
          [placeholder]="'knowledge_base.price_observations.edit.description_placeholder' | translate"
        />
      </div>
    </div>

    <p class="observation-edit__corrected" *ngIf="isCorrected">
      ⚑ {{ "knowledge_base.price_observations.edit.corrected_on" | translate }}
      {{ observation.corrected_at | date: 'dd/MM/yyyy' }}
      <ng-container *ngIf="observation.corrected_by"> — {{ observation.corrected_by }}</ng-container>
    </p>

    <!-- ONE sentence for whatever holds SAVE back: an empty price, a product
         typed but never picked from the list, or a named audience with nobody
         in it. The last was the only one that used to say anything. -->
    <p class="observation-edit__note observation-edit__note--warn" *ngIf="blockedReasonKey">
      {{ blockedReasonKey | translate }}
    </p>

    <!-- Says what SAVE will do, once. It names "the original quote above", so
         it only holds when there is one — and it says nothing the ⚑ line does
         not already say on a row that has been corrected. -->
    <p class="observation-edit__note observation-edit__note--in-place" *ngIf="hasQuote && !isCorrected">
      {{ "knowledge_base.price_observations.edit.in_place_note" | translate }}
    </p>
  </div>
</app-shared-dialog>
